// scene.ts — the pond: water, shore, lilypads, a camera that zooms into a pad and back out.
// Owns: PixiJS app, world container, pad sprites, camera tweening, ripples.
// Calls: creatures.ts (populate + tick), sound.ts (splash on zoom).
// Called by: main.ts (setLilypads, focusPad, zoomOut, on('padTap')).

import { Application, Assets, Circle, Container, Graphics, Sprite, Texture, Text, TextStyle, Ticker, FederatedPointerEvent } from 'pixi.js';
import { Creatures } from './creatures';
import { sound } from './sound';
import { Avatar, Intro, Wanderer, type AvatarStyle } from './avatar';

export interface PadData {
  id: string;
  title: string;
  dateLabel: string;
  subtitle: string;
  x: number; // 0..1
  y: number; // 0..1
  size: number;
  mine: boolean;
  coverStyle?: 'classic' | 'blossom' | 'evening';
  /** Authenticated event thumbnail only. Locked/public invitation pads never request it. */
  coverUrl?: string;
  locked?: boolean; // shown but not joined (the "join with code" pad)
}

export const WORLD_W = 2000;
export const WORLD_H = 1400;
/** The water occupies the inner rectangle; the ring outside is shore. */
export const SHORE = 150;

type Listener = (id: string) => void;

export class PondScene {
  /** Each entry page has one Pixi renderer; its system scheduler must follow that scene's visibility. */
  private static systemOwner: PondScene | null = null;
  readonly app = new Application();
  readonly world = new Container();
  private water = new Graphics();
  private shore = new Graphics();
  private ripples = new Container();
  private padLayer = new Container();
  private pads = new Map<string, { root: Container; data: PadData; phase: number }>();
  private creatures!: Creatures;
  private listeners: Listener[] = [];
  private camTarget = { x: WORLD_W / 2, y: WORLD_H / 2, scale: 1 };
  private cam = { x: WORLD_W / 2, y: WORLD_H / 2, scale: 1 };
  private focused: string | null = null;
  private t = 0;
  /** Characters (docs/COSMETICS.md): one wanderer on the bottom bank, and a screen-space intro on entry screens. */
  private charLayer = new Container();
  readonly overlay = new Container();
  private wanderer: Wanderer | null = null;
  private intro: Intro | null = null;
  private characterStyle: AvatarStyle | null = null;
  private leafTexture: Texture | null = null;
  private coverTextures = new Map<string, Texture>();
  private coverLoads = new Map<string, { image: HTMLImageElement; promise: Promise<Texture | null>; finish: (texture: Texture | null) => void }>();
  private padRevisions = new WeakMap<Container, number>();
  private glints = new Graphics();
  private intros: Intro[] = [];
  private guests: Array<{ avatar: Avatar; padId: string; phase: number }> = [];
  private reduced = false;
  private paused = false;
  private presented = true;
  private initialized = false;
  private destroyed = false;
  private suspendedSystem: { started: boolean; autoStart: boolean } | null = null;
  private motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  private motionOverride: boolean | null = null;
  private readonly onResize = () => {
    for (const pad of this.pads.values()) this.restylePad(pad.root, pad.data);
    if (this.focused) this.focusPad(this.focused, false);
    else this.fitCamera(true);
  };
  private readonly onVisibility = () => this.syncPlayback();
  private readonly onMotion = () => this.applyMotion();

  async init(canvas: HTMLCanvasElement): Promise<void> {
    if (PondScene.systemOwner && PondScene.systemOwner !== this) throw new Error('A document can present only one pond scene.');
    PondScene.systemOwner = this;
    try { await this.app.init({ canvas, resizeTo: window, autoStart: false, antialias: true, backgroundAlpha: 1, background: 0x507b48, resolution: Math.min(2, window.devicePixelRatio || 1), autoDensity: true }); }
    catch (error) { if (PondScene.systemOwner === this) PondScene.systemOwner = null; throw error; }
    this.initialized = true;
    if (this.destroyed) { this.app.destroy(true, { children: true, texture: false, textureSource: false }); this.initialized = false; return; }
    this.world.addChild(this.shore, this.water);
    this.drawShore();
    this.drawWater();
    try {
      const [background, leaf] = await Promise.all([
        Assets.load<Texture>('/art/pond-garden.webp'), Assets.load<Texture>('/art/lily-leaf.webp'),
      ]);
      if (this.destroyed) return;
      const garden = new Sprite(background);
      garden.width = WORLD_W; garden.height = WORLD_H;
      garden.eventMode = 'none';
      this.shore.clear();
      this.water.clear();
      this.world.addChild(garden);
      this.leafTexture = leaf;
    } catch (error) { console.warn('Pond art unavailable; using the drawn garden.', error); }
    if (this.destroyed) return;
    this.world.addChild(this.glints, this.ripples);
    this.creatures = new Creatures(this.world, WORLD_W, WORLD_H, SHORE);
    this.world.addChild(this.padLayer);
    this.creatures.addAboveLayer(this.world);
    this.world.addChild(this.charLayer);
    this.app.stage.addChild(this.world, this.overlay);
    this.app.stage.eventMode = 'static';
    this.app.stage.hitArea = this.app.screen;
    this.app.stage.on('pointertap', (e: FederatedPointerEvent) => {
      if (e.target === this.app.stage) {
        const p = this.world.toLocal(e.global);
        if (this.isWater(p.x, p.y)) { this.ripple(p.x, p.y, 1); sound.drip(); }
      }
    });
    window.addEventListener('resize', this.onResize);
    document.addEventListener('visibilitychange', this.onVisibility);
    this.motionQuery.addEventListener('change', this.onMotion);
    this.applyMotion();
    this.fitCamera(false);
    this.app.ticker.add((tk) => this.tick(tk.deltaMS / 1000));
    this.syncPlayback();
  }

  onPadTap(fn: Listener): void {
    this.listeners.push(fn);
  }

  /** Replace the pads on the water. Positions are 0..1 across the water rectangle. */
  setLilypads(data: PadData[]): void {
    for (const [id, p] of this.pads) if (!data.find((d) => d.id === id)) { p.root.destroy({ children: true }); this.pads.delete(id); }
    data.forEach((d, i) => {
      const existing = this.pads.get(d.id);
      if (existing) { existing.data = d; this.restylePad(existing.root, d); return; }
      const root = this.buildPad(d);
      this.padLayer.addChild(root);
      this.pads.set(d.id, { root, data: d, phase: i * 1.7 });
    });
    this.creatures.setPadAnchors([...this.pads.values()].map((p) => ({ x: p.root.x, y: p.root.y, r: 110 * p.data.size })));
    this.pruneCovers(new Set(data.filter((pad) => !pad.locked && pad.coverUrl).map((pad) => pad.coverUrl!)));
  }

  padWorldPos(d: PadData): { x: number; y: number } {
    let u = (Math.max(0, Math.min(1, d.x)) - 0.5) * 2;
    let v = (Math.max(0, Math.min(1, d.y)) - 0.5) * 2;
    const distance = Math.hypot(u, v), edge = 0.77;
    if (distance > edge) { u *= edge / distance; v *= edge / distance; }
    const radius = 110 * d.size + 8;
    const fits = (factor: number) => {
      for (let i = 0; i < 24; i++) {
        const a = i * Math.PI / 12;
        if ((u * factor + Math.cos(a) * radius / 850) ** 2 + (v * factor + Math.sin(a) * radius / 550) ** 2 > 0.99) return false;
      }
      return true;
    };
    if (!fits(1)) {
      let low = 0, high = 1;
      for (let i = 0; i < 9; i++) { const mid = (low + high) / 2; if (fits(mid)) low = mid; else high = mid; }
      u *= low; v *= low;
    }
    return { x: WORLD_W / 2 + u * 850, y: WORLD_H / 2 + v * 550 };
  }

  /** Camera flies in until the pad fills most of the shorter screen edge. */
  focusPad(id: string, feedback = true): void {
    const p = this.pads.get(id);
    if (!p) return;
    this.focused = id;
    const r = 130 * p.data.size;
    const scale = (Math.min(this.app.screen.width, this.app.screen.height) * 0.42) / r;
    this.camTarget = { x: p.root.x, y: p.root.y - r * 0.15, scale };
    if (feedback) { this.ripple(p.root.x, p.root.y, 2); sound.splash(); }
    if (this.reduced || this.paused) { this.cam = { ...this.camTarget }; this.applyCamera(); }
  }

  zoomOut(): void {
    this.focused = null;
    this.fitCamera(true);
  }

  get focusedId(): string | null {
    return this.focused;
  }

  get reducedMotion(): boolean { return this.reduced; }
  get isPaused(): boolean { return this.paused; }

  setReducedMotion(reduced: boolean | null): void { this.motionOverride = reduced; this.applyMotion(); }

  setPaused(paused: boolean): void { this.paused = paused; this.syncPlayback(); }

  /** Switching to Explorer suspends the scene without changing the visitor's pause preference. */
  setPresented(presented: boolean): void { this.presented = presented; this.syncPlayback(); }

  private applyMotion(): void {
    this.reduced = this.motionOverride ?? this.motionQuery.matches;
    this.creatures?.setReducedMotion(this.reduced);
    if (this.wanderer) this.wanderer.avatar.reducedMotion = this.reduced;
    for (const intro of this.intros) intro.avatar.reducedMotion = this.reduced;
    for (const guest of this.guests) guest.avatar.reducedMotion = this.reduced;
    if (this.reduced) {
      this.cam = { ...this.camTarget };
      for (const ripple of [...this.ripples.children]) ripple.destroy();
    }
  }

  private syncPlayback(): void {
    const stopped = this.paused || !this.presented || document.hidden;
    sound.setScenePaused(this.paused || !this.presented);
    if (!this.initialized || this.destroyed) return;
    const events = this.app.renderer.events;
    if (!this.presented || document.hidden) {
      // app.stop() leaves Pixi's separate stationary-pointer EventsTicker running on Ticker.system.
      // The documented null target detaches this renderer's DOM handlers and that ticker listener.
      // Pixi 8.22's declaration omits null even though its public API and implementation accept it.
      if (events.domElement) events.setTargetElement(null!);
      // Pixi 8.22's renderer SchedulerSystem also owns a permanent system-ticker listener for GC.
      // Preserve its queued maintenance tasks, but stop their frame driver while the sole scene is absent.
      if (!this.suspendedSystem) this.suspendedSystem = { started: Ticker.system.started, autoStart: Ticker.system.autoStart };
      Ticker.system.autoStart = false;
      Ticker.system.stop();
    } else {
      // Rebind while auto-start is still suppressed, then restore the exact previous driver flags.
      if (!events.domElement) events.setTargetElement(this.app.canvas as HTMLCanvasElement);
      this.restoreSystemTicker();
    }
    if (stopped) this.app.stop(); else this.app.start();
  }

  private restoreSystemTicker(): void {
    const previous = this.suspendedSystem;
    if (!previous) return;
    this.suspendedSystem = null;
    Ticker.system.autoStart = previous.autoStart;
    if (previous.started) Ticker.system.start(); else Ticker.system.stop();
  }

  /** Synthetic cast for the public art playground, never a claim about real member presence. */
  setPreviewAvatars(styles: AvatarStyle[]): void {
    for (const guest of this.guests) guest.avatar.root.destroy({ children: true });
    this.guests = [];
    const pads = [...this.pads.values()];
    styles.forEach((style, i) => {
      const pad = pads[i % pads.length];
      if (!pad) return;
      const avatar = new Avatar(style, 1.08);
      avatar.reducedMotion = this.reduced;
      avatar.root.eventMode = 'static'; avatar.root.cursor = 'help';
      avatar.root.on('pointerover', () => avatar.setLabel(style.name));
      avatar.root.on('pointerout', () => avatar.setLabel(null));
      this.charLayer.addChild(avatar.root);
      this.guests.push({ avatar, padId: pad.data.id, phase: i * 2.3 });
    });
  }

  destroy(): void {
    this.destroyed = true;
    this.pruneCovers(new Set());
    window.removeEventListener('resize', this.onResize);
    document.removeEventListener('visibilitychange', this.onVisibility);
    this.motionQuery.removeEventListener('change', this.onMotion);
    if (this.initialized) this.app.destroy(true, { children: true, texture: false, textureSource: false });
    this.initialized = false;
    // Restore shared library defaults after this renderer removes its own maintenance listeners.
    this.restoreSystemTicker();
    if (PondScene.systemOwner === this) PondScene.systemOwner = null;
  }

  // ---------- characters ----------

  /** Put a character on the pond's bottom bank (null removes it). Only ponds whose theme allows it call this. */
  setCharacter(style: AvatarStyle | null): void {
    this.characterStyle = style;
    for (const intro of this.intros) intro.avatar.root.destroy({ children: true });
    this.intros = []; this.intro = null;
    if (this.wanderer) { this.wanderer.avatar.root.destroy({ children: true }); this.wanderer = null; }
    if (!style) return;
    const bankY = WORLD_H - SHORE + 52;
    this.wanderer = new Wanderer(style, SHORE + 140, WORLD_W - SHORE - 140, bankY, 1.25, (x, y, s) => this.ripple(x, y, s));
    this.wanderer.avatar.reducedMotion = this.reduced;
    this.charLayer.addChild(this.wanderer.avatar.root);
  }

  /**
   * Entry-screen greeting in screen space: walks on from the left, waves or gives a thumbs-up and a wink, waits,
   * walks off. Returns a handle to send her off early. No-op without a character.
   */
  introCharacter(): { walkOff: () => void } {
    const style = this.characterStyle;
    if (!style) return { walkOff: () => {} };
    if (this.intro) this.intro.walkOff();
    // Every retiring intro stays in the update/destroy list, including rapid replays.
    if (this.intros.length >= 2) this.intros.shift()!.avatar.root.destroy({ children: true });
    const narrow = () => this.app.screen.width < 760;
    const size = () => Math.max(1.1, Math.min(2.6, this.app.screen.height / 330)) * (narrow() ? 0.8 : 1);
    const intro = new Intro(
      style,
      () => (narrow() ? this.app.screen.width * 0.5 : Math.min(this.app.screen.width * 0.24, 360)),
      () => (narrow() ? this.app.screen.height * 0.44 : this.app.screen.height - 40),
      size,
      () => this.wanderer?.greet(),
    );
    this.intro = intro;
    intro.avatar.reducedMotion = this.reduced;
    this.intros.push(intro);
    this.overlay.addChild(intro.avatar.root);
    return { walkOff: () => intro.walkOff() };
  }

  // ---------- drawing ----------

  private drawWater(): void {
    const g = this.water;
    g.clear();
    // Deep centre, lighter edges: layered ellipses approximate a radial gradient.
    const cx = WORLD_W / 2, cy = WORLD_H / 2;
    const rx = WORLD_W / 2 - SHORE + 40, ry = WORLD_H / 2 - SHORE + 40;
    const stops = [0x4aa3d6, 0x3d94c9, 0x3186bd, 0x2a7ab0, 0x256ea2, 0x216395];
    stops.forEach((c, i) => {
      const k = 1 - i * 0.14;
      g.ellipse(cx, cy, rx * k, ry * k).fill(c);
    });
    // sun glints
    for (let i = 0; i < 60; i++) {
      const a = Math.random() * Math.PI * 2, d = Math.random();
      g.ellipse(cx + Math.cos(a) * rx * 0.9 * d, cy + Math.sin(a) * ry * 0.9 * d, 10 + Math.random() * 30, 2 + Math.random() * 2).fill({ color: 0xffffff, alpha: 0.08 + Math.random() * 0.1 });
    }
    g.eventMode = 'none';
  }

  private drawShore(): void {
    const g = this.shore;
    g.clear();
    g.rect(-400, -400, WORLD_W + 800, WORLD_H + 800).fill(0x6e9a4a); // grass far
    g.rect(-200, -200, WORLD_W + 400, WORLD_H + 400).fill(0x7fae55); // grass
    g.roundRect(SHORE - 70, SHORE - 70, WORLD_W - 2 * SHORE + 140, WORLD_H - 2 * SHORE + 140, 300).fill(0xc9b27a); // sand
    g.roundRect(SHORE - 40, SHORE - 40, WORLD_W - 2 * SHORE + 80, WORLD_H - 2 * SHORE + 80, 280).fill(0x9c8559); // wet mud
    // pebbles
    for (let i = 0; i < 90; i++) {
      const onX = Math.random() < 0.5;
      const x = onX ? Math.random() * WORLD_W : Math.random() < 0.5 ? SHORE - 60 + Math.random() * 30 : WORLD_W - SHORE + 30 + Math.random() * 30;
      const y = onX ? (Math.random() < 0.5 ? SHORE - 60 + Math.random() * 30 : WORLD_H - SHORE + 30 + Math.random() * 30) : Math.random() * WORLD_H;
      g.ellipse(x, y, 4 + Math.random() * 6, 3 + Math.random() * 4).fill(0x8a7a66);
    }
    // reeds along the top and right banks
    for (let i = 0; i < 70; i++) {
      const side = Math.random();
      const x = side < 0.6 ? 200 + Math.random() * (WORLD_W - 400) : WORLD_W - SHORE + 10 + Math.random() * 40;
      const y = side < 0.6 ? SHORE - 30 + Math.random() * 50 : 200 + Math.random() * (WORLD_H - 400);
      const h = 50 + Math.random() * 70;
      g.moveTo(x, y).lineTo(x + (Math.random() - 0.5) * 12, y - h).stroke({ color: 0x3f7a2c, width: 3 });
      if (Math.random() < 0.5) g.ellipse(x + (Math.random() - 0.5) * 8, y - h, 4, 12).fill(0x6b4b2a); // cattail
    }
    g.eventMode = 'none';
  }

  private buildPad(d: PadData): Container {
    const root = new Container();
    const pos = this.padWorldPos(d);
    root.position.set(pos.x, pos.y);
    root.eventMode = 'static';
    root.cursor = 'pointer';
    root.on('pointertap', () => this.listeners.forEach((f) => f(d.id)));
    root.on('pointerover', () => (root.scale.set(1.04)));
    root.on('pointerout', () => (root.scale.set(1)));
    this.restylePad(root, d);
    return root;
  }

  private restylePad(root: Container, d: PadData): void {
    const revision = (this.padRevisions.get(root) ?? 0) + 1;
    this.padRevisions.set(root, revision);
    root.removeChildren().forEach((c) => c.destroy({ children: true }));
    const r = 110 * d.size;
    const g = new Graphics();
    // shadow on water
    g.ellipse(6, 10, r * 1.02, r * 0.95).fill({ color: 0x0a2a3a, alpha: 0.25 });
    root.addChild(g);
    if (this.leafTexture) {
      const leaf = new Sprite(this.leafTexture);
      leaf.anchor.set(0.5); leaf.width = r * 2.12; leaf.height = r * 2.12;
      leaf.tint = d.locked ? 0xa9b992 : d.coverStyle === 'evening' ? 0xb6ded8 : d.coverStyle === 'blossom' ? 0xf1ead1 : d.mine ? 0xffffff : 0xe4f2c9;
      root.addChild(leaf);
    } else {
    // the pad: a circle with a notch cut toward the top-right
    const notch = -Math.PI / 4;
    g.moveTo(0, 0);
    g.arc(0, 0, r, notch + 0.35, notch - 0.35 + Math.PI * 2);
    g.lineTo(0, 0);
    g.fill(d.locked ? 0x7d9d6a : d.mine ? 0x3fa34d : 0x4caf5a);
    // veins
    for (let i = 0; i < 9; i++) {
      const a = notch + 0.5 + (i / 9) * (Math.PI * 2 - 1);
      g.moveTo(0, 0).lineTo(Math.cos(a) * r * 0.92, Math.sin(a) * r * 0.92).stroke({ color: 0x2b7a3a, width: 2, alpha: 0.6 });
    }
    // highlight
    g.ellipse(-r * 0.3, -r * 0.3, r * 0.35, r * 0.2).fill({ color: 0xffffff, alpha: 0.12 });
    // rim
    g.arc(0, 0, r, notch + 0.35, notch - 0.35 + Math.PI * 2).stroke({ color: 0x2b7a3a, width: 3 });
    }
    root.hitArea = new Circle(0, 0, r);

    // blossom on owner's pads
    if (d.mine && !d.locked) {
      const f = new Graphics();
      for (let i = 0; i < 6; i++) {
        const a = (i / 6) * Math.PI * 2;
        f.ellipse(Math.cos(a) * 12, Math.sin(a) * 12, 13, 7).fill(0xf6b5d4);
      }
      f.circle(0, 0, 7).fill(0xf9d85e);
      f.position.set(-r * 0.45, -r * 0.5);
      root.addChild(f);
    }

    const overviewScale = Math.min(this.app.screen.width / WORLD_W, this.app.screen.height / WORLD_H) * (this.app.screen.height > this.app.screen.width ? 1.25 : 1);
    const title = new Text({
      text: d.title.length > 22 ? d.title.slice(0, 21) + '…' : d.title,
      style: new TextStyle({ fontFamily: 'Segoe UI, system-ui, sans-serif', fontSize: Math.max(16, r * 0.17, 13 / overviewScale), fontWeight: '700', fill: 0xffffff, stroke: { color: 0x31562a, width: 4 }, align: 'center', wordWrap: true, wordWrapWidth: r * 1.5 }),
    });
    title.anchor.set(0.5);
    title.position.set(0, -r * 0.31);
    root.addChild(title);

    const sub = new Text({ text: d.locked ? 'Tap to join' : d.dateLabel + (d.subtitle ? `\n${d.subtitle}` : ''), style: new TextStyle({ fontFamily: 'Segoe UI, system-ui, sans-serif', fontSize: Math.max(12, r * 0.085), fill: 0xfaffea, stroke: { color: 0x31562a, width: 3 }, align: 'center', wordWrap: true, wordWrapWidth: r * 1.5 }) });
    sub.label = 'subtitle';
    sub.anchor.set(0.5);
    sub.position.set(0, r * 0.01);
    root.addChild(sub);
    if (!d.locked && d.coverUrl && /^\/api\/media\/[a-f0-9-]+\/thumb$/i.test(d.coverUrl)) {
      const url = d.coverUrl;
      void this.coverTexture(url).then((texture) => {
        // Restyling, deleting a pad, changing accounts or revoking its cover invalidates older callbacks.
        const current = this.pads.get(d.id);
        if (!texture || this.destroyed || this.coverTextures.get(url) !== texture || current?.root !== root || current.data.locked || current.data.coverUrl !== url || this.padRevisions.get(root) !== revision) return;
        const radius = r * 0.7, cy = r * 0.04;
        const layer = new Container(); layer.eventMode = 'none';
        const photo = new Sprite(texture); photo.anchor.set(0.5); photo.position.set(0, cy);
        const scale = radius * 2 / Math.min(texture.width, texture.height);
        photo.scale.set(scale);
        const mask = new Graphics().circle(0, cy, radius).fill(0xffffff);
        mask.eventMode = 'none'; photo.mask = mask;
        const edge = new Graphics().circle(0, cy, radius).stroke({ color: 0xf5f5d9, width: 2, alpha: 0.65 });
        // Keep the event title/date readable on busy photographs without baking words into the image.
        const labelWash = new Graphics().roundRect(-r * 0.79, -r * 0.52, r * 1.58, r * 0.79, r * 0.12).fill({ color: 0x24442d, alpha: 0.62 });
        layer.addChild(photo, mask, edge, labelWash);
        // Shadow and leaf remain below the photo; flower and live labels stay above it.
        root.addChildAt(layer, Math.min(2, root.children.length));
      });
    }
  }

  private coverTexture(url: string): Promise<Texture | null> {
    const cached = this.coverTextures.get(url);
    if (cached) return Promise.resolve(cached);
    const pending = this.coverLoads.get(url);
    if (pending) return pending.promise;
    const image = new Image();
    let finish!: (texture: Texture | null) => void;
    const promise = new Promise<Texture | null>((resolve) => { finish = resolve; });
    const load = { image, promise, finish };
    this.coverLoads.set(url, load);
    image.onload = () => {
      if (this.destroyed || this.coverLoads.get(url) !== load) { finish(null); return; }
      this.coverLoads.delete(url); image.onload = image.onerror = null;
      if (!image.naturalWidth || !image.naturalHeight) { finish(null); return; }
      try { const texture = Texture.from(image); this.coverTextures.set(url, texture); finish(texture); }
      catch { finish(null); }
    };
    image.onerror = () => { if (this.coverLoads.get(url) === load) this.coverLoads.delete(url); image.onload = image.onerror = null; finish(null); };
    image.src = url;
    return promise;
  }

  private pruneCovers(allowed: Set<string>): void {
    for (const [url, load] of this.coverLoads) if (!allowed.has(url)) {
      this.coverLoads.delete(url); load.image.onload = load.image.onerror = null;
      load.image.removeAttribute('src'); load.finish(null);
    }
    for (const [url, texture] of this.coverTextures) if (!allowed.has(url)) { this.coverTextures.delete(url); texture.destroy(true); }
  }

  ripple(x: number, y: number, strength = 1): void {
    if (this.reduced || this.paused || !this.isWater(x, y)) return;
    if (this.ripples.children.length >= 48) this.ripples.children[0].destroy();
    const g = new Graphics();
    g.position.set(x, y);
    (g as Graphics & { life: number; strength: number }).life = 0;
    (g as Graphics & { life: number; strength: number }).strength = strength;
    this.ripples.addChild(g);
  }

  // ---------- camera ----------

  private fitCamera(animated: boolean): void {
    const sw = this.app.screen.width, sh = this.app.screen.height;
    const scale = Math.min(sw / WORLD_W, sh / WORLD_H) * (sh > sw ? 1.25 : 1);
    this.camTarget = { x: WORLD_W / 2, y: WORLD_H / 2, scale: Math.max(scale, 0.12) };
    if (!animated || this.reduced || this.paused) { this.cam = { ...this.camTarget }; this.applyCamera(); }
  }

  private applyCamera(): void {
    const sw = this.app.screen.width, sh = this.app.screen.height;
    this.world.scale.set(this.cam.scale);
    this.world.position.set(sw / 2 - this.cam.x * this.cam.scale, sh / 2 - this.cam.y * this.cam.scale);
  }

  private tick(dt: number): void {
    dt = Math.max(0, Math.min(dt, 0.05));
    if (!this.reduced) this.t += dt;
    // camera ease
    const k = 1 - Math.pow(0.001, dt); // ~ fast exponential ease
    this.cam.x += (this.camTarget.x - this.cam.x) * k;
    this.cam.y += (this.camTarget.y - this.cam.y) * k;
    this.cam.scale += (this.camTarget.scale - this.cam.scale) * k;
    this.applyCamera();

    // pads bob gently
    for (const p of this.pads.values()) {
      const base = this.padWorldPos(p.data);
      p.root.position.set(base.x + (this.reduced ? 0 : Math.sin(this.t * 0.6 + p.phase) * 3), base.y + (this.reduced ? 0 : Math.cos(this.t * 0.45 + p.phase) * 3));
      p.root.rotation = this.reduced ? 0 : Math.sin(this.t * 0.3 + p.phase) * 0.015;
      const subtitle = p.root.getChildByLabel('subtitle');
      if (subtitle) subtitle.visible = 220 * p.data.size * this.cam.scale >= 185;
    }

    // ripples expand and fade
    for (const c of [...this.ripples.children] as Array<Graphics & { life: number; strength: number }>) {
      c.life += dt;
      const life = c.life / (1.6 * c.strength);
      if (life >= 1) { c.destroy(); continue; }
      c.clear();
      for (let i = 0; i < 3; i++) {
        const r = (20 + i * 22) * (0.4 + life * 1.6) * c.strength;
        c.circle(0, 0, r).stroke({ color: 0xffffff, width: 2.5 - i * 0.6, alpha: (1 - life) * (0.5 - i * 0.12) });
      }
    }
    // random ambient ripples (rain-drop, fish nibbling)
    if (!this.reduced && Math.random() < dt * 0.35) {
      this.ripple(SHORE + Math.random() * (WORLD_W - 2 * SHORE), SHORE + Math.random() * (WORLD_H - 2 * SHORE), 0.6 + Math.random() * 0.6);
    }

    this.glints.clear();
    for (let i = 0; i < 26; i++) {
      const a = i * 2.399, d = Math.sqrt((i + 1) / 28) * 0.77;
      const x = WORLD_W / 2 + Math.cos(a) * 850 * d;
      const y = WORLD_H / 2 + Math.sin(a) * 550 * d;
      const phase = this.t * 0.75 + i * 1.7;
      this.glints.ellipse(x + Math.sin(phase) * 6, y, 10 + i % 13, 1.1).fill({ color: 0xd8fff1, alpha: 0.04 + (Math.sin(phase) + 1) * 0.035 });
    }
    this.creatures.setPadAnchors([...this.pads.values()].map((p) => ({ x: p.root.x, y: p.root.y, r: 110 * p.data.size })));
    this.creatures.tick(dt, this.t, (x, y, s) => this.ripple(x, y, s));
    this.wanderer?.update(Math.min(dt, 0.1));
    for (const guest of this.guests) {
      const pad = this.pads.get(guest.padId);
      if (!pad) { guest.avatar.root.visible = false; continue; }
      guest.avatar.root.visible = true;
      const r = pad.data.size * 110;
      const walk = !this.reduced && Math.sin(this.t * 0.23 + guest.phase) > 0.25;
      if (walk) guest.phase += dt * 0.16;
      const phase = guest.phase;
      guest.avatar.setAction(walk ? 'walk' : 'idle');
      const nextX = pad.root.x + Math.sin(phase) * r * 0.67;
      guest.avatar.motionSpeed = walk && dt > 0 ? Math.abs(nextX - guest.avatar.root.x) / (dt * guest.avatar.size) : 0;
      guest.avatar.facing = Math.cos(phase) > 0 ? 1 : -1;
      guest.avatar.root.position.set(nextX, pad.root.y + r * 0.82);
      guest.avatar.update(dt);
    }
    for (const intro of [...this.intros]) {
      intro.update(dt, this.app.screen.width);
      if (intro.done) {
        intro.avatar.root.destroy({ children: true });
        this.intros = this.intros.filter((item) => item !== intro);
        if (this.intro === intro) this.intro = null;
      }
    }
  }

  private isWater(x: number, y: number): boolean {
    return ((x - WORLD_W / 2) / 850) ** 2 + ((y - WORLD_H / 2) / 550) ** 2 <= 1;
  }
}
